import { useState } from 'react';
import { useGraphQL } from '../graphql';
import Modal from './Modal';
import {
    TrashIcon,
    WrenchScrewdriverIcon,
    ClockIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const JobManagementModal = ({ isOpen, onClose, onAction }) => {
    const { executeMutation, mutations, loading } = useGraphQL();
    const [activeTab, setActiveTab] = useState('pending');
    const [cleanupDays, setCleanupDays] = useState(30);
    const [stuckHours, setStuckHours] = useState(2);
    const [pendingLimit, setPendingLimit] = useState(20);
    const [result, setResult] = useState(null);

    // Scheduled job management state
    const [scheduleJobId, setScheduleJobId] = useState('');
    const [scheduleCron, setScheduleCron] = useState('');
    const [scheduleParams, setScheduleParams] = useState('{}');

    const handleCleanup = async () => {
        if (!confirm(`Delete all completed/failed jobs older than ${cleanupDays} days?`)) return;

        try {
            const res = await executeMutation(mutations.adminCleanupOldJobs, {
                olderThanDays: parseInt(cleanupDays)
            });

            if (res.adminCleanupOldJobs.success) {
                setResult({
                    type: 'success',
                    message: `Successfully deleted ${res.adminCleanupOldJobs.deletedCount} old jobs`
                });
                onAction(`Cleaned up ${res.adminCleanupOldJobs.deletedCount} old jobs`);
            } else {
                setResult({
                    type: 'error',
                    message: res.adminCleanupOldJobs.error || 'Cleanup failed'
                });
            }
        } catch (err) {
            setResult({
                type: 'error',
                message: err.message
            });
        }
    };

    const handleFixStuck = async () => {
        if (!confirm(`Mark all jobs running for more than ${stuckHours} hours as failed?`)) return;

        try {
            const res = await executeMutation(mutations.adminFixStuckJobs, {
                olderThanHours: parseInt(stuckHours)
            });

            if (res.adminFixStuckJobs.success) {
                setResult({
                    type: 'success',
                    message: `Fixed ${res.adminFixStuckJobs.fixedCount} stuck jobs`,
                    details: res.adminFixStuckJobs.fixedJobs
                });
                onAction(`Fixed ${res.adminFixStuckJobs.fixedCount} stuck jobs`);
            } else {
                setResult({
                    type: 'error',
                    message: res.adminFixStuckJobs.error || 'Fix failed'
                });
            }
        } catch (err) {
            setResult({
                type: 'error',
                message: err.message
            });
        }
    };

    const handleGetPending = async () => {
        try {
            const res = await executeMutation(mutations.adminGetPendingJobs, {
                limit: parseInt(pendingLimit)
            });

            if (res.adminGetPendingJobs.success) {
                setResult({
                    type: 'info',
                    message: `Found ${res.adminGetPendingJobs.count} pending jobs`,
                    details: res.adminGetPendingJobs.jobs
                });
            } else {
                setResult({
                    type: 'error',
                    message: res.adminGetPendingJobs.error || 'Query failed'
                });
            }
        } catch (err) {
            setResult({
                type: 'error',
                message: err.message
            });
        }
    };

    const handleEnableScheduledJob = async () => {
        if (!scheduleJobId.trim()) {
            setResult({ type: 'error', message: 'Job ID is required' });
            return;
        }

        try {
            const res = await executeMutation(mutations.adminEnableScheduledJob, {
                jobId: scheduleJobId
            });

            if (res.adminEnableScheduledJob.success) {
                setResult({
                    type: 'success',
                    message: `Successfully enabled scheduled job ${scheduleJobId}`
                });
                onAction(`Enabled scheduled job ${scheduleJobId}`);
            } else {
                setResult({
                    type: 'error',
                    message: res.adminEnableScheduledJob.error || 'Failed to enable job'
                });
            }
        } catch (err) {
            setResult({
                type: 'error',
                message: err.message
            });
        }
    };

    const handleDisableScheduledJob = async () => {
        if (!scheduleJobId.trim()) {
            setResult({ type: 'error', message: 'Job ID is required' });
            return;
        }

        try {
            const res = await executeMutation(mutations.adminDisableScheduledJob, {
                jobId: scheduleJobId
            });

            if (res.adminDisableScheduledJob.success) {
                setResult({
                    type: 'success',
                    message: `Successfully disabled scheduled job ${scheduleJobId}`
                });
                onAction(`Disabled scheduled job ${scheduleJobId}`);
            } else {
                setResult({
                    type: 'error',
                    message: res.adminDisableScheduledJob.error || 'Failed to disable job'
                });
            }
        } catch (err) {
            setResult({
                type: 'error',
                message: err.message
            });
        }
    };

    const handleUpdateScheduledJob = async () => {
        if (!scheduleJobId.trim()) {
            setResult({ type: 'error', message: 'Job ID is required' });
            return;
        }

        try {
            let parameters = null;
            if (scheduleParams.trim() && scheduleParams.trim() !== '{}') {
                try {
                    parameters = JSON.parse(scheduleParams);
                } catch (e) {
                    setResult({ type: 'error', message: 'Invalid JSON in parameters' });
                    return;
                }
            }

            const variables = {
                jobId: scheduleJobId,
                cronSchedule: scheduleCron.trim() || null,
                parameters: parameters
            };

            const res = await executeMutation(mutations.adminUpdateScheduledJob, variables);

            if (res.adminUpdateScheduledJob.success) {
                setResult({
                    type: 'success',
                    message: `Successfully updated scheduled job ${scheduleJobId}`
                });
                onAction(`Updated scheduled job ${scheduleJobId}`);
            } else {
                setResult({
                    type: 'error',
                    message: res.adminUpdateScheduledJob.error || 'Failed to update job'
                });
            }
        } catch (err) {
            setResult({
                type: 'error',
                message: err.message
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Job Management" size="lg">
            <div className="space-y-6">
                {/* Tabs */}
                <div className="flex space-x-2 border-b border-gray-700">
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`px-4 py-2 ${
                            activeTab === 'pending'
                                ? 'border-b-2 border-white text-white font-mono text-xs font-bold uppercase'
                                : 'text-gray-400 hover:text-gray-300'
                        }`}
                    >
                        <ClockIcon className="h-5 w-5 inline mr-2" />
                        Pending Jobs
                    </button>
                    <button
                        onClick={() => setActiveTab('stuck')}
                        className={`px-4 py-2 ${
                            activeTab === 'stuck'
                                ? 'border-b-2 border-white text-white font-mono text-xs font-bold uppercase'
                                : 'text-gray-400 hover:text-gray-300'
                        }`}
                    >
                        <WrenchScrewdriverIcon className="h-5 w-5 inline mr-2" />
                        Fix Stuck
                    </button>
                    <button
                        onClick={() => setActiveTab('cleanup')}
                        className={`px-4 py-2 ${
                            activeTab === 'cleanup'
                                ? 'border-b-2 border-white text-white font-mono text-xs font-bold uppercase'
                                : 'text-gray-400 hover:text-gray-300'
                        }`}
                    >
                        <TrashIcon className="h-5 w-5 inline mr-2" />
                        Cleanup
                    </button>
                    <button
                        onClick={() => setActiveTab('scheduled')}
                        className={`px-4 py-2 ${
                            activeTab === 'scheduled'
                                ? 'border-b-2 border-white text-white font-mono text-xs font-bold uppercase'
                                : 'text-gray-400 hover:text-gray-300'
                        }`}
                    >
                        <ClockIcon className="h-5 w-5 inline mr-2" />
                        Scheduled
                    </button>
                </div>

                {/* Content */}
                <div className="min-h-[200px]">
                    {activeTab === 'pending' && (
                        <div className="space-y-4">
                            <p className="text-gray-300">
                                View all pending jobs that are waiting to be executed.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Limit
                                </label>
                                <input
                                    type="number"
                                    value={pendingLimit}
                                    onChange={(e) => setPendingLimit(e.target.value)}
                                    min="1"
                                    max="100"
                                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                />
                            </div>
                            <button
                                onClick={handleGetPending}
                                disabled={loading}
                                className="w-full px-4 py-2 bg-primary text-white hover:bg-primary/80 disabled:opacity-50"
                            >
                                {loading ? 'Loading...' : 'Get Pending Jobs'}
                            </button>
                        </div>
                    )}

                    {activeTab === 'stuck' && (
                        <div className="space-y-4">
                            <div className="bg-yellow-50 border border-yellow-200 p-3">
                                <div className="flex items-start">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-700 mr-2 mt-0.5" />
                                    <p className="text-yellow-700 text-sm">
                                        This will mark all jobs that have been running for more than the specified hours as failed.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Hours Running
                                </label>
                                <input
                                    type="number"
                                    value={stuckHours}
                                    onChange={(e) => setStuckHours(e.target.value)}
                                    min="1"
                                    max="72"
                                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                />
                                <p className="text-sm text-gray-400 mt-1">
                                    Jobs running for more than {stuckHours} hours will be marked as failed
                                </p>
                            </div>
                            <button
                                onClick={handleFixStuck}
                                disabled={loading}
                                className="w-full px-4 py-2 bg-yellow-600 text-white hover:bg-yellow-500 disabled:opacity-50"
                            >
                                {loading ? 'Processing...' : 'Fix Stuck Jobs'}
                            </button>
                        </div>
                    )}

                    {activeTab === 'cleanup' && (
                        <div className="space-y-4">
                            <div className="bg-red-50 border border-red-200 p-3">
                                <div className="flex items-start">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-red-700 mr-2 mt-0.5" />
                                    <p className="text-red-700 text-sm">
                                        This will permanently delete completed and failed jobs older than the specified days.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Older Than (days)
                                </label>
                                <input
                                    type="number"
                                    value={cleanupDays}
                                    onChange={(e) => setCleanupDays(e.target.value)}
                                    min="1"
                                    max="365"
                                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                />
                                <p className="text-sm text-gray-400 mt-1">
                                    Jobs completed/failed before {new Date(Date.now() - cleanupDays * 24 * 60 * 60 * 1000).toLocaleDateString()} will be deleted
                                </p>
                            </div>
                            <button
                                onClick={handleCleanup}
                                disabled={loading}
                                className="w-full px-4 py-2 bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
                            >
                                {loading ? 'Deleting...' : 'Cleanup Old Jobs'}
                            </button>
                        </div>
                    )}

                    {activeTab === 'scheduled' && (
                        <div className="space-y-4">
                            <p className="text-gray-300">
                                Manage scheduled jobs: enable, disable, or update their cron schedule and parameters.
                            </p>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Job ID *
                                </label>
                                <input
                                    type="text"
                                    value={scheduleJobId}
                                    onChange={(e) => setScheduleJobId(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 text-white p-2 font-mono"
                                    placeholder="Enter job ID"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Cron Schedule (for update)
                                </label>
                                <input
                                    type="text"
                                    value={scheduleCron}
                                    onChange={(e) => setScheduleCron(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 text-white p-2 font-mono"
                                    placeholder="0 0 * * *"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Leave empty to keep existing schedule
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Parameters (for update)
                                </label>
                                <textarea
                                    value={scheduleParams}
                                    onChange={(e) => setScheduleParams(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 text-white p-2 h-24 font-mono text-sm"
                                    placeholder="{}"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Leave as {"{}"} to keep existing parameters
                                </p>
                                <p className="text-xs text-gray-400">
                                    Assessment examples: assess_tender -{">"} {"{\"companyId\":\"<company-id>\"}"}; assess_all_tenders -{">"} {"{\"companyId\":\"<company-id>\",\"skipExisting\":true,\"minRating\":10,\"limit\":100}"}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Text extraction: extract_document_text -{">"} {"{\"documentId\":\"<document-id>\"}"}; extract_all_documents_text -{">"} {"{\"limit\":1000}"}
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={handleEnableScheduledJob}
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white hover:bg-green-500 disabled:opacity-50"
                                >
                                    {loading ? 'Processing...' : 'Enable'}
                                </button>
                                <button
                                    onClick={handleDisableScheduledJob}
                                    disabled={loading}
                                    className="px-4 py-2 bg-yellow-600 text-white hover:bg-yellow-500 disabled:opacity-50"
                                >
                                    {loading ? 'Processing...' : 'Disable'}
                                </button>
                                <button
                                    onClick={handleUpdateScheduledJob}
                                    disabled={loading}
                                    className="px-4 py-2 bg-primary text-white hover:bg-primary/80 disabled:opacity-50"
                                >
                                    {loading ? 'Processing...' : 'Update'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Result Display */}
                {result && (
                    <div className={`p-4 ${
                        result.type === 'success' ? 'bg-green-50 border border-green-200' :
                            result.type === 'error' ? 'bg-red-50 border border-red-200' :
                                'bg-blue-50 border border-blue-200'
                    }`}>
                        <p className={`font-medium ${
                            result.type === 'success' ? 'text-green-700' :
                                result.type === 'error' ? 'text-red-700' :
                                    'text-blue-700'
                        }`}>
                            {result.message}
                        </p>
                        {result.details && (
                            <div className="mt-2 max-h-40 overflow-y-auto">
                                <pre className="text-xs text-gray-300">
                                    {JSON.stringify(result.details, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 font-mono text-xs font-bold uppercase bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-white"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default JobManagementModal;
